import type { AxiosError, AxiosInstance } from "axios";
import Axios from "axios";
import * as Sentry from "@sentry/react";
import { readSentryUserMeta } from "./sentry-user-meta";

const DEDUPE_WINDOW_MS = 10_000;
const MAX_SANITIZE_DEPTH = 6;
const MAX_SANITIZE_KEYS = 50;
const MAX_SANITIZE_ARRAY_ITEMS = 50;
const MAX_SANITIZE_STRING_LENGTH = 1000;
const recentErrorFingerprints = new Map<string, number>();

const SECRET_KEY_PATTERNS = [
  "password",
  "passwd",
  "passphrase",
  "token",
  "accessToken",
  "refreshToken",
  "idToken",
  "apiKey",
  "secret",
  "clientSecret",
  "authorization",
  "proxyAuthorization",
  "cookie",
  "setCookie",
  "session",
  "jwt",
  "credential",
  "csrf",
  "xsrf",
  "otp",
  "totp",
  "mfaCode",
  "inviteToken",
];
const SECRET_TEXT_KEY_PATTERNS = [
  ...SECRET_KEY_PATTERNS,
  "auth_token",
  "session_token",
  "x_auth_token",
  "x_api_key",
  "csrf_token",
  "xsrf_token",
  "mfa_code",
  "invite_token",
];

const isAbortOrCancelError = (error: AxiosError): boolean => {
  if (Axios.isCancel(error)) return true;
  if (error.code === "ERR_CANCELED") return true;
  if (error.name === "CanceledError") return true;
  if (typeof error.message === "string" && error.message.toLowerCase().includes("canceled")) return true;
  return false;
};

const safeParseJson = (value: unknown): unknown => {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const getSafeTypeLabel = (value: unknown): string => {
  if (value === null) return "[null]";
  if (value === undefined) return "[undefined]";
  if (typeof value === "function") return "[function]";
  if (typeof value === "symbol") return "[symbol]";
  if (typeof value === "bigint") return "[bigint]";
  if (value instanceof Date) return "[date]";
  if (value instanceof Error) return "[error]";
  if (typeof Blob !== "undefined" && value instanceof Blob) return "[blob]";
  if (value && typeof value === "object") {
    return `[${value.constructor?.name || "object"}]`;
  }
  return `[${typeof value}]`;
};

const normalizeSecretKey = (key: string): string => key.toLowerCase().replace(/[^a-z0-9]/g, "");

const SECRET_KEY_MATCHERS = SECRET_KEY_PATTERNS.map(normalizeSecretKey);
const SECRET_TEXT_KEY_PATTERN =
  SECRET_TEXT_KEY_PATTERNS
    .flatMap((key) => {
      const normalized = normalizeSecretKey(key);
      const separatorFriendly = key
        .replace(/[A-Z]/g, (letter) => `[_-]?${letter.toLowerCase()}`)
        .replace(/_/g, "[_-]?");
      return normalized === separatorFriendly ? [normalized] : [normalized, separatorFriendly];
    })
    .join("|");
const SECRET_DOUBLE_QUOTED_ASSIGNMENT_RE = new RegExp(
  `(["']?(?:${SECRET_TEXT_KEY_PATTERN})["']?\\s*[:=]\\s*)"[^"]*"`,
  "gi",
);
const SECRET_SINGLE_QUOTED_ASSIGNMENT_RE = new RegExp(
  `(["']?(?:${SECRET_TEXT_KEY_PATTERN})["']?\\s*[:=]\\s*)'[^']*'`,
  "gi",
);
const SECRET_UNQUOTED_ASSIGNMENT_RE = new RegExp(
  `(["']?(?:${SECRET_TEXT_KEY_PATTERN})["']?\\s*[:=]\\s*)[^"',&\\s}]+`,
  "gi",
);

const isSecretKey = (key: string): boolean => {
  const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, "");
  return SECRET_KEY_MATCHERS.some(
    (pattern) => normalizedKey === pattern || normalizedKey.includes(pattern),
  );
};

const truncateString = (value: string): string => {
  if (value.length <= MAX_SANITIZE_STRING_LENGTH) return value;
  return `${value.slice(0, MAX_SANITIZE_STRING_LENGTH)}...[truncated]`;
};

const redactSecretText = (value: string): string => {
  const redacted = value
    .replace(
      /\b(authorization\s*[:=]\s*)(bearer|basic)\s+[a-z0-9._~+/=-]+/gi,
      "$1$2 [redacted]",
    )
    .replace(
      /\b(bearer|basic)\s+[a-z0-9._~+/=-]+/gi,
      "$1 [redacted]",
    )
    .replace(
      /\beyJ[a-z0-9_-]+\.[a-z0-9_-]+\.[a-z0-9_-]+\b/gi,
      "[redacted-jwt]",
    )
    .replace(SECRET_DOUBLE_QUOTED_ASSIGNMENT_RE, '$1"[redacted]"')
    .replace(SECRET_SINGLE_QUOTED_ASSIGNMENT_RE, "$1'[redacted]'")
    .replace(SECRET_UNQUOTED_ASSIGNMENT_RE, "$1[redacted]");

  return truncateString(redacted);
};

const redactSecretPath = (pathname: string): string =>
  pathname.replace(
    /(\/(?:reset-password|password-reset|magic-link|magic-links|invite|invites|invitation|invitations|token|tokens|session|sessions)\/)[^/?#]+/gi,
    "$1[redacted]",
  );

const redactSearchParams = (params: URLSearchParams): URLSearchParams => {
  const redactedParams = new URLSearchParams(params);

  for (const key of Array.from(redactedParams.keys())) {
    const value = redactedParams.get(key) ?? "";
    redactedParams.set(key, isSecretKey(key) ? "[redacted]" : redactSecretText(value));
  }

  return redactedParams;
};

const getUrlWithRedactedSecrets = (url: string | undefined): string | null => {
  if (!url) return null;

  try {
    const isAbsoluteUrl = /^[a-z][a-z\d+\-.]*:/i.test(url);
    const parsed = new URL(url, window.location.origin);

    if (parsed.username) {
      parsed.username = "[redacted]";
    }
    if (parsed.password) {
      parsed.password = "[redacted]";
    }

    parsed.search = redactSearchParams(parsed.searchParams).toString();

    if (parsed.hash) {
      const hashValue = parsed.hash.slice(1);
      if (hashValue.includes("=")) {
        parsed.hash = redactSearchParams(new URLSearchParams(hashValue)).toString();
      } else {
        parsed.hash = redactSecretText(hashValue);
      }
    }

    const pathname = redactSecretPath(parsed.pathname);

    return isAbsoluteUrl
      ? `${parsed.origin}${pathname}${parsed.search}${parsed.hash}`
      : `${pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return redactSecretText(url);
  }
};

const summarizeFileField = (value: Blob) => ({
  name: typeof File !== "undefined" && value instanceof File ? value.name : null,
  type: value.type || null,
  size: value.size,
});

const sanitizeValue = (
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>(),
): unknown => {
  if (value === null || value === undefined) return value;
  if (typeof value === "string") return redactSecretText(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (typeof value !== "object") return getSafeTypeLabel(value);
  if (depth >= MAX_SANITIZE_DEPTH) return "[max-depth]";
  if (seen.has(value)) return "[circular]";

  seen.add(value);

  if (value instanceof URLSearchParams) {
    const params: Record<string, unknown> = {};
    const counts = new Map<string, number>();

    for (const [key, paramValue] of value.entries()) {
      if (Object.keys(params).length >= MAX_SANITIZE_KEYS) {
        params.__truncated = true;
        break;
      }

      if (isSecretKey(key)) {
        params[key] = "[redacted]";
        continue;
      }

      const count = counts.get(key) ?? 0;
      counts.set(key, count + 1);
      if (count === 0) {
        params[key] = sanitizeValue(paramValue, depth + 1, seen);
      } else if (Array.isArray(params[key])) {
        (params[key] as unknown[]).push(sanitizeValue(paramValue, depth + 1, seen));
      } else {
        params[key] = [params[key], sanitizeValue(paramValue, depth + 1, seen)];
      }
    }

    return params;
  }

  if (typeof FormData !== "undefined" && value instanceof FormData) {
    const formData: Record<string, unknown> = {};

    for (const [key, formValue] of value.entries()) {
      if (Object.keys(formData).length >= MAX_SANITIZE_KEYS) {
        formData.__truncated = true;
        break;
      }

      if (isSecretKey(key)) {
        formData[key] = "[redacted]";
      } else if (typeof Blob !== "undefined" && formValue instanceof Blob) {
        formData[key] = summarizeFileField(formValue);
      } else {
        formData[key] = sanitizeValue(formValue, depth + 1, seen);
      }
    }

    return formData;
  }

  if (typeof Blob !== "undefined" && value instanceof Blob) {
    return summarizeFileField(value);
  }

  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) {
    const sanitizedItems = value
      .slice(0, MAX_SANITIZE_ARRAY_ITEMS)
      .map((item) => sanitizeValue(item, depth + 1, seen));

    if (value.length > MAX_SANITIZE_ARRAY_ITEMS) {
      sanitizedItems.push("[truncated]");
    }

    return sanitizedItems;
  }

  if (Object.getPrototypeOf(value) !== Object.prototype) {
    return getSafeTypeLabel(value);
  }

  const sanitizedRecord: Record<string, unknown> = {};
  const entries = Object.entries(value as Record<string, unknown>);

  for (const [key, itemValue] of entries.slice(0, MAX_SANITIZE_KEYS)) {
    sanitizedRecord[key] = isSecretKey(key)
      ? "[redacted]"
      : sanitizeValue(itemValue, depth + 1, seen);
  }

  if (entries.length > MAX_SANITIZE_KEYS) {
    sanitizedRecord.__truncated = true;
  }

  return sanitizedRecord;
};

const getSanitizedRequestContext = (
  params: unknown,
  data: unknown,
  headers: unknown,
): Record<string, unknown> | null => {
  if (params === undefined && data === undefined && headers === undefined) return null;

  return {
    params: params === undefined ? null : sanitizeValue(params),
    data: data === undefined ? null : sanitizeValue(safeParseJson(data)),
    headers: headers === undefined ? null : sanitizeValue(getHeaderRecord(headers)),
  };
};

const getHeaderRecord = (headers: unknown): unknown => {
  if (!headers || typeof headers !== "object") return headers;

  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }

  if ("toJSON" in headers && typeof headers.toJSON === "function") {
    return headers.toJSON();
  }

  return headers;
};

const getRequestPath = (url: string | undefined): string => {
  if (!url) return "unknown";

  try {
    const parsed = new URL(url, window.location.origin);
    return redactSecretPath(parsed.pathname);
  } catch {
    return redactSecretPath(redactSecretText(url.split("?")[0] ?? url));
  }
};

const getApiErrorDetails = (
  payload: unknown,
): {
  code: string | null
  message: string | null
  payload: unknown
} => {
  const parsedPayload = safeParseJson(payload);

  if (typeof parsedPayload === "string") {
    const [maybeCode, ...messageParts] = parsedPayload.split(":");
    const normalizedCode = maybeCode?.trim() || null;
    const normalizedMessage = messageParts.join(":").trim() || parsedPayload;

    return {
      code: normalizedCode,
      message: normalizedMessage,
      payload: parsedPayload,
    };
  }

  if (Array.isArray(parsedPayload)) {
    const firstMessage =
      parsedPayload.find((item) => typeof item?.message === "string")?.message ?? null;

    return {
      code: null,
      message: firstMessage,
      payload: parsedPayload,
    };
  }

  if (parsedPayload && typeof parsedPayload === "object") {
    const record = parsedPayload as Record<string, unknown>;
    const errorValue = record.error;
    const messageValue = record.message;

    if (typeof errorValue === "string") {
      const nestedError = getApiErrorDetails(errorValue);
      return {
        code: nestedError.code,
        message: nestedError.message,
        payload: parsedPayload,
      };
    }

    return {
      code: typeof record.code === "string" ? record.code : null,
      message: typeof messageValue === "string" ? messageValue : null,
      payload: parsedPayload,
    };
  }

  return {
    code: null,
    message: null,
    payload: parsedPayload,
  };
};

const makeFingerprint = (error: AxiosError): string => {
  const method = error.config?.method ?? "unknown";
  const path = getRequestPath(error.config?.url);
  const status = error.response?.status ?? "none";
  const code = error.code ?? "none";
  const apiError = getApiErrorDetails(error.response?.data);
  const apiErrorCode = apiError.code ? redactSecretText(apiError.code) : null;
  const apiErrorMessage = apiError.message ? redactSecretText(apiError.message) : null;
  return `${method}:${path}:${status}:${code}:${apiErrorCode ?? "no_api_code"}:${apiErrorMessage ?? "no_api_message"}`;
};

const shouldCaptureAxiosError = (error: AxiosError): boolean => {
  if (isAbortOrCancelError(error)) return false;

  const now = Date.now();
  const fingerprint = makeFingerprint(error);
  const lastSeenAt = recentErrorFingerprints.get(fingerprint);
  if (lastSeenAt && now - lastSeenAt < DEDUPE_WINDOW_MS) return false;

  recentErrorFingerprints.set(fingerprint, now);
  for (const [key, ts] of recentErrorFingerprints.entries()) {
    if (now - ts > DEDUPE_WINDOW_MS) {
      recentErrorFingerprints.delete(key);
    }
  }

  return true;
};

const getNavigatorOnline = (): boolean | null => {
  if (typeof navigator === "undefined") return null;
  return navigator.onLine;
};

const getNetworkClassification = (error: AxiosError): string | null => {
  if (error.response) return null;
  if (error.code === "ECONNABORTED") return "timeout";
  if (error.code === "ERR_NETWORK") return "err_network";
  return "no_response";
};

export const attachSentryAxiosErrorInterceptor = (instance: AxiosInstance): void => {
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (shouldCaptureAxiosError(error)) {
        const user = readSentryUserMeta();
        const requestPath = getRequestPath(error.config?.url);
        const hasResponse = Boolean(error.response);
        const apiError = getApiErrorDetails(error.response?.data);
        const apiErrorCode = apiError.code ? redactSecretText(apiError.code) : null;
        const apiErrorMessage = apiError.message ? redactSecretText(apiError.message) : null;
        const requestContext = getSanitizedRequestContext(
          error.config?.params,
          error.config?.data,
          error.config?.headers,
        );

        Sentry.withScope((scope) => {
          scope.setTag("source", "axios");
          scope.setTag("kind", hasResponse ? "api_error" : "api_network_error");
          scope.setTag("endpoint", requestPath);
          scope.setTag("http_status", String(error.response?.status ?? "none"));
          scope.setTag("axios_code", error.code ?? "none");
          if (apiErrorCode) {
            scope.setTag("api_error_code", apiErrorCode);
          }
          scope.setFingerprint([
            "axios",
            error.config?.method ?? "unknown",
            requestPath,
            String(error.response?.status ?? "none"),
            apiErrorCode ?? "no_api_code",
            apiErrorMessage ?? "no_api_message",
          ]);
          scope.setContext("http", {
            method: error.config?.method ?? null,
            url: getUrlWithRedactedSecrets(error.config?.url),
            path: requestPath,
            status: error.response?.status ?? null,
            code: error.code ?? null,
            hasResponse,
            errorName: error.name ?? null,
            errorMessage: redactSecretText(error.message ?? ""),
            timeout: error.config?.timeout ?? null,
            baseURL: getUrlWithRedactedSecrets(error.config?.baseURL),
            navigatorOnline: getNavigatorOnline(),
          });
          scope.setContext("api_error", {
            code: apiErrorCode,
            message: apiErrorMessage,
          });
          scope.setContext("organization", {
            headerOrgId: error.config?.headers?.["x-organization-id"] ?? null,
            storedOrgId: user.orgId ?? null,
          });
          scope.setContext("response", {
            data: hasResponse ? sanitizeValue(apiError.payload) : null,
            headers: sanitizeValue(getHeaderRecord(error.response?.headers)),
          });
          if (!hasResponse) {
            scope.setContext("network", {
              classification: getNetworkClassification(error),
              message: redactSecretText(error.message ?? ""),
              online: getNavigatorOnline(),
              timeoutMs: error.config?.timeout ?? null,
              baseURL: getUrlWithRedactedSecrets(error.config?.baseURL),
            });
          }
          if (requestContext) {
            scope.setContext("request", requestContext);
          }
          if (user.id || user.email) {
            scope.setUser({
              id: user.id,
              email: user.email,
            });
          }
          const capturedError = new Error(redactSecretText(error.message || "Axios request failed"));
          capturedError.name = error.name || "AxiosError";
          capturedError.stack = error.stack;
          Sentry.captureException(capturedError);
        });
      }
      return Promise.reject(error);
    },
  );
};
